import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useActiveCompany } from "../context/ActiveCompanyContext.jsx";

/**
 * Header picker for the working company. Locked (🔒) while a bill is being created or edited.
 * variant "button" (header actions) or "crumb" (desktop breadcrumb).
 */
export function CompanyPicker({ variant = "button" }) {
  const navigate = useNavigate();
  const { companies, activeCompany, activeCompanyId, setActiveCompany, isLocked } = useActiveCompany();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [anchor, setAnchor] = useState(null); // button position: the menu is drawn over the page so nothing clips it

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const reposition = () => setOpen(false);
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!note) return undefined;
    const t = setTimeout(() => setNote(""), 3000);
    return () => clearTimeout(t);
  }, [note]);

  useEffect(() => { if (isLocked) setOpen(false); }, [isLocked]);

  if (!activeCompany) return null;
  const name = activeCompany.name || "Company";
  const shortName = name.split(" ")[0];

  const toggle = () => {
    if (isLocked) {
      setNote("Finish or close the bill to switch company");
      return;
    }
    const r = ref.current?.getBoundingClientRect();
    if (r) setAnchor({ top: r.bottom + 6, left: r.left, right: window.innerWidth - r.right });
    setOpen((o) => !o);
  };

  return (
    <div ref={ref} className={variant === "crumb" ? "crumb company-crumb" : undefined} style={{ position: "relative", display: "inline-flex", minWidth: 0, flexShrink: 0 }}>
      {variant === "crumb" ? (
        <button
          type="button"
          className={`company-crumb-btn ${open ? "open" : ""}`}
          data-testid="company-picker-crumb"
          onClick={toggle}
          title={isLocked ? "Finish or close the bill to switch company" : "Switch company"}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span aria-hidden="true">🏢</span>
          <span className="company-crumb-name">{name}</span>
          <span className="company-crumb-caret" aria-hidden="true">{isLocked ? "🔒" : "▾"}</span>
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-secondary btn-sm header-btn"
          data-testid="company-picker"
          onClick={toggle}
          title={isLocked ? "Finish or close the bill to switch company" : `Working company: ${name}`}
          aria-label={`Working company: ${name}`}
          aria-haspopup="menu"
          aria-expanded={open}
          style={{ display: "flex", alignItems: "center", gap: "4px", maxWidth: "140px", overflow: "hidden", whiteSpace: "nowrap" }}
        >
          <span>🏢</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.75rem" }}>{shortName}</span>
          <span style={{ fontSize: "0.7rem" }}>{isLocked ? "🔒" : "▾"}</span>
        </button>
      )}

      {open && anchor && createPortal(
        <div
          ref={menuRef}
          role="menu"
          data-testid="company-menu"
          className="company-menu"
          style={{ top: anchor.top, ...(variant === "crumb" ? { left: anchor.left } : { right: anchor.right }) }}
        >
          <div className="company-menu-title">Working company</div>
          {companies.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitemradio"
              aria-checked={c.id === activeCompanyId}
              className={`company-menu-item ${c.id === activeCompanyId ? "active" : ""}`}
              onClick={() => { setActiveCompany(c.id); setOpen(false); }}
            >
              <span className="company-menu-check">{c.id === activeCompanyId ? "✓" : ""}</span>
              <span className="company-menu-name">{c.name}</span>
              {c.isDefault && <span className="company-menu-tag">default</span>}
            </button>
          ))}
          <div className="company-menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="company-menu-item company-menu-manage"
            onClick={() => { setOpen(false); navigate("/settings#companies"); }}
          >
            ⚙️ Manage companies
          </button>
        </div>,
        document.body
      )}

      {note && createPortal(
        <div role="status" style={{ position: "fixed", top: (ref.current?.getBoundingClientRect().bottom || 50) + 6, ...(variant === "crumb" ? { left: ref.current?.getBoundingClientRect().left || 12 } : { right: window.innerWidth - (ref.current?.getBoundingClientRect().right || window.innerWidth - 12) }), zIndex: 1200, whiteSpace: "nowrap", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.4rem 0.65rem", fontSize: "0.78rem", color: "var(--warning, #b45309)", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
          🔒 {note}
        </div>,
        document.body
      )}
    </div>
  );
}

export default CompanyPicker;
